import type { Language } from "@/i18n/translations";

export type SupportedCountryCode = "PT" | "ES" | "FR" | "GB" | "AU" | "NL";

export interface LocaleOption {
  locale: string;
  country: SupportedCountryCode;
  currency: string;
  languageCode: string;
  uiLanguage: Language;
  label: string;
  countryLabel: string;
  languageLabel: string;
  normStandard: string;
}

export const LOCALE_OPTIONS: LocaleOption[] = [
  {
    locale: "pt-PT",
    country: "PT",
    currency: "EUR",
    languageCode: "pt",
    uiLanguage: "pt",
    label: "Português (Portugal)",
    countryLabel: "Portugal",
    languageLabel: "Português",
    normStandard: "NP EN",
  },
  {
    locale: "es-ES",
    country: "ES",
    currency: "EUR",
    languageCode: "es",
    uiLanguage: "es",
    label: "Español (España)",
    countryLabel: "España",
    languageLabel: "Español",
    normStandard: "UNE EN",
  },
  {
    locale: "fr-FR",
    country: "FR",
    currency: "EUR",
    languageCode: "fr",
    uiLanguage: "en",
    label: "Français (France)",
    countryLabel: "France",
    languageLabel: "Français",
    normStandard: "NF EN",
  },
  {
    locale: "en-GB",
    country: "GB",
    currency: "GBP",
    languageCode: "en",
    uiLanguage: "en",
    label: "English (United Kingdom)",
    countryLabel: "United Kingdom",
    languageLabel: "English",
    normStandard: "BS EN",
  },
  {
    locale: "en-AU",
    country: "AU",
    currency: "AUD",
    languageCode: "en",
    uiLanguage: "en",
    label: "English (Australia)",
    countryLabel: "Australia",
    languageLabel: "English",
    normStandard: "AS",
  },
  {
    locale: "nl-NL",
    country: "NL",
    currency: "EUR",
    languageCode: "nl",
    uiLanguage: "en",
    label: "Nederlands (Nederland)",
    countryLabel: "Nederland",
    languageLabel: "Nederlands",
    normStandard: "NEN",
  },
];

const optionByLocale = new Map(
  LOCALE_OPTIONS.map((option) => [option.locale.toLowerCase(), option]),
);

const optionByCountry = new Map(
  LOCALE_OPTIONS.map((option) => [option.country, option]),
);

const COUNTRY_LOCALE_FALLBACKS: Record<string, string> = {
  PT: "pt-PT",
  ES: "es-ES",
  GB: "en-GB",
  UK: "en-GB",
  FR: "fr-FR",
  DE: "de-DE",
  IT: "it-IT",
  NL: "nl-NL",
  BE: "fr-BE",
  GENERIC: "en-GB",
};

export interface ResolvedLocaleContext {
  userLocale: string;
  userCountry: SupportedCountryCode;
  userCurrency: string;
  languageCode: string;
  uiLanguage: Language;
  languageLabel: string;
  countryLabel: string;
  normStandard: string;
}

export function getLocaleOption(locale: string | null | undefined): LocaleOption | null {
  if (!locale) return null;
  return optionByLocale.get(locale.trim().toLowerCase()) ?? null;
}

export function getCountryOption(country: string | null | undefined): LocaleOption | null {
  if (!country) return null;
  return optionByCountry.get(country.trim().toUpperCase() as SupportedCountryCode) ?? null;
}

export function normalizeExplicitLocale(locale: string | null | undefined): string | null {
  if (!locale) return null;

  try {
    const normalized = new Intl.Locale(locale.trim());
    return normalized.region ? normalized.baseName : null;
  } catch {
    return null;
  }
}

export function countryToLocale(
  countryCode: string | null | undefined,
  fallbackLocale = "en-GB",
): string {
  const normalizedCountry = countryCode?.trim().toUpperCase();
  if (!normalizedCountry) return fallbackLocale;

  const localeFromCatalog = getCountryOption(normalizedCountry)?.locale;
  if (localeFromCatalog) return localeFromCatalog;

  return COUNTRY_LOCALE_FALLBACKS[normalizedCountry] ?? fallbackLocale;
}

export function getBrowserLocale(): string {
  if (typeof navigator === "undefined") return "en-GB";
  return navigator.language || "en-GB";
}

export function resolveLocaleContext(input: {
  profileLocale?: string | null;
  profileCountry?: string | null;
  organizationCountry?: string | null;
  organizationCurrency?: string | null;
  browserLocale?: string | null;
}): ResolvedLocaleContext {
  const localeOption =
    getLocaleOption(input.profileLocale) ??
    getCountryOption(input.profileCountry) ??
    getCountryOption(input.organizationCountry) ??
    getLocaleOption(input.browserLocale) ??
    getCountryOption("GB")!;

  return {
    userLocale: localeOption.locale,
    userCountry: localeOption.country,
    userCurrency: input.organizationCurrency?.trim() || localeOption.currency,
    languageCode: localeOption.languageCode,
    uiLanguage: localeOption.uiLanguage,
    languageLabel: localeOption.languageLabel,
    countryLabel: localeOption.countryLabel,
    normStandard: localeOption.normStandard,
  };
}

export function localeToUiLanguage(locale: string | null | undefined): Language {
  return getLocaleOption(locale)?.uiLanguage ?? "en";
}

export function uiLanguageToDefaultLocale(language: Language): string {
  if (language === "pt") return "pt-PT";
  if (language === "es") return "es-ES";
  return "en-GB";
}

export function getLocalesForCountry(country: string | null | undefined): LocaleOption[] {
  const normalized = country?.trim().toUpperCase();
  return LOCALE_OPTIONS.filter((option) => option.country === normalized);
}
